import React, { useCallback, useMemo } from "react";
import { Form, useFormik, FormikProvider } from "formik";
import { StandardModalHeader } from "@design-system/Modal/Modal";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { CountInput } from "@design-system/CountInput/CountInput";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { HStack } from "@design-system/HStack/HStack";
import { useScopeSwitch } from "@hooks/keyboard/useScopeSwitch";
import { HOTKEYS_SCOPES } from "@hooks/keyboard/useKeyboardCommand";
import { generateDayOptions } from "@components/accounting/journalEntryDrawer/helpers";
import {
    NativeInvoiceCustomTermType,
    NativeInvoiceCustomTermValue,
} from "@common/helpers/invoice-terms.helper";
import styles from "./NativeInvoiceCustomTermModal.module.scss";

// The 3 custom-term options QBO offers, styled/behaviorally consistent with
// the existing custom JE recurrence modal (RecurrenceModalContent).
const CUSTOM_TERM_TYPE_OPTIONS: Array<{
    value: NativeInvoiceCustomTermType;
    label: string;
}> = [
    {
        value: NativeInvoiceCustomTermType.DUE_IN_DAYS,
        label: "Due in",
    },
    {
        value: NativeInvoiceCustomTermType.DUE_BY_DAY_OF_CURRENT_MONTH,
        label: "Due by the",
    },
    {
        value: NativeInvoiceCustomTermType.DUE_BY_DAY_OF_NEXT_MONTH,
        label: "Due by the",
    },
];

const DEFAULT_DAYS = 30;
const DEFAULT_DAY_OF_MONTH = 1;
// Arbitrary month passed to generateDayOptions purely to get a 1-31 day list
// (the helper only needs a month to compute days-in-month for clamping in the
// JE recurrence flow; a 31-day month here means the picker always offers 31
// options, matching the "day of month" semantics — actual clamping to the
// target month happens in deriveDueDateFromTermSelection).
const DAY_OF_MONTH_OPTIONS = generateDayOptions(0);

interface Props {
    initialValue: NativeInvoiceCustomTermValue | null;
    onSave: (value: NativeInvoiceCustomTermValue) => void;
    onCancel: () => void;
}

export const NativeInvoiceCustomTermModal: React.FC<Props> = ({
    initialValue,
    onSave,
    onCancel,
}) => {
    useScopeSwitch(HOTKEYS_SCOPES.GENERAL_MODAL);

    const formik = useFormik<NativeInvoiceCustomTermValue>({
        initialValues: initialValue ?? {
            type: NativeInvoiceCustomTermType.DUE_IN_DAYS,
            days: DEFAULT_DAYS,
            dayOfMonth: null,
        },
        onSubmit: onSave,
    });

    const handleTypeChange = useCallback(
        (value: NativeInvoiceCustomTermType) => {
            formik.setFieldValue("type", value);
            if (value === NativeInvoiceCustomTermType.DUE_IN_DAYS) {
                formik.setFieldValue("dayOfMonth", null);
                if (!formik.values.days) {
                    formik.setFieldValue("days", DEFAULT_DAYS);
                }
            } else {
                formik.setFieldValue("days", null);
                if (!formik.values.dayOfMonth) {
                    formik.setFieldValue("dayOfMonth", DEFAULT_DAY_OF_MONTH);
                }
            }
        },
        [formik],
    );

    const canSubmit = useMemo(() => {
        if (formik.values.type === NativeInvoiceCustomTermType.DUE_IN_DAYS) {
            return (
                formik.values.days != null &&
                Number.isInteger(formik.values.days) &&
                formik.values.days >= 0
            );
        }
        return formik.values.dayOfMonth != null;
    }, [formik.values.type, formik.values.days, formik.values.dayOfMonth]);

    return (
        <FormikProvider value={formik}>
            <Form>
                <StandardModalHeader>Custom terms</StandardModalHeader>
                <RadioGroup<NativeInvoiceCustomTermType>
                    name="custom-term-type"
                    aria-label="Custom term type"
                    value={formik.values.type}
                    onValueChange={handleTypeChange}
                    className={styles.block}
                >
                    {CUSTOM_TERM_TYPE_OPTIONS.map((option) => (
                        <div className={styles.line} key={option.value}>
                            <div className={styles.leftLabel}>
                                <Radio
                                    value={option.value}
                                    label={option.label}
                                />
                            </div>
                            <div className={styles.fieldContainer}>
                                {option.value ===
                                    NativeInvoiceCustomTermType.DUE_IN_DAYS && (
                                    <>
                                        <CountInput
                                            disabled={
                                                formik.values.type !==
                                                option.value
                                            }
                                            min={0}
                                            value={formik.values.days ?? 0}
                                            onValueChange={(value) =>
                                                formik.setFieldValue(
                                                    "days",
                                                    value,
                                                )
                                            }
                                            className={styles.countField}
                                        />
                                        <span className={styles.suffixText}>
                                            days
                                        </span>
                                    </>
                                )}
                                {(option.value ===
                                    NativeInvoiceCustomTermType.DUE_BY_DAY_OF_CURRENT_MONTH ||
                                    option.value ===
                                        NativeInvoiceCustomTermType.DUE_BY_DAY_OF_NEXT_MONTH) && (
                                    <>
                                        <Picker.Select
                                            items={DAY_OF_MONTH_OPTIONS}
                                            searchable
                                            searchPlaceholder="Search day..."
                                            emptyLabel="No matching day"
                                            disabled={
                                                formik.values.type !==
                                                option.value
                                            }
                                            value={
                                                formik.values.dayOfMonth ?? null
                                            }
                                            onValueChange={(dayOfMonth) => {
                                                if (dayOfMonth != null) {
                                                    formik.setFieldValue(
                                                        "dayOfMonth",
                                                        dayOfMonth,
                                                    );
                                                }
                                            }}
                                            placeholder="Select day"
                                            trigger={
                                                <Picker.Button
                                                    variant="primary"
                                                    size="xl"
                                                    className={styles.daySelect}
                                                />
                                            }
                                        />
                                        <span className={styles.suffixText}>
                                            day of the{" "}
                                            {option.value ===
                                            NativeInvoiceCustomTermType.DUE_BY_DAY_OF_CURRENT_MONTH
                                                ? "current"
                                                : "next"}{" "}
                                            month
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </RadioGroup>

                <HStack as="footer" justify="end" gap={12} className="mt-4">
                    <Button variant="secondary" onClick={onCancel} size="xl">
                        Cancel
                    </Button>
                    <WithHotkey
                        hotKey="mod+enter"
                        hotKeyOptions={{
                            description: "Save",
                            scopes: [HOTKEYS_SCOPES.GENERAL_MODAL],
                        }}
                    >
                        <Button
                            disabled={!canSubmit}
                            onClick={() => formik.handleSubmit()}
                            type="submit"
                            variant="primary"
                            size="xl"
                        >
                            Save
                        </Button>
                    </WithHotkey>
                </HStack>
            </Form>
        </FormikProvider>
    );
};
